import { API_ORIGIN } from './baseUrl';
// Admin API Client
// Handles all admin portal API calls with JWT authentication

// In dev: '/api/admin' (proxied to localhost:3000 by Vite). In production:
// VITE_API_BASE_URL is set to the absolute API origin at build time.
const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/admin`;

// ============================================================================
// AUTH STATE MANAGEMENT
// ============================================================================

let accessToken: string | null = null;
let refreshToken: string | null = null;

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('admin_access_token', access);
  localStorage.setItem('admin_refresh_token', refresh);
}

export function loadTokens() {
  accessToken = localStorage.getItem('admin_access_token');
  refreshToken = localStorage.getItem('admin_refresh_token');
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('admin_access_token');
  localStorage.removeItem('admin_refresh_token');
}

export function isAuthenticated(): boolean {
  loadTokens();
  return !!accessToken;
}

// ============================================================================
// API FETCH WRAPPER
// ============================================================================

interface AdminApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
  success?: boolean;
}

async function adminFetch<T>(
  endpoint: string,
  options?: RequestInit,
  requiresAuth: boolean = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (requiresAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${ADMIN_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - try refresh token
  if (response.status === 401 && refreshToken && requiresAuth) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      const retryResponse = await fetch(`${ADMIN_API_BASE}${endpoint}`, {
        ...options,
        headers,
      });
      return handleResponse<T>(retryResponse);
    } else {
      clearTokens();
      throw new Error('Session expired. Please log in again.');
    }
  }

  return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response): Promise<T> {
  const json: AdminApiResponse<T> = await response.json();

  if (!response.ok) {
    throw new Error(json.error || json.message || `HTTP ${response.status}`);
  }

  return json.data as T;
}

async function tryRefreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${ADMIN_API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) return false;

    const json = await response.json();
    if (json.data?.access_token) {
      accessToken = json.data.access_token;
      if (json.data.refresh_token) {
        refreshToken = json.data.refresh_token;
      }
      setTokens(accessToken!, refreshToken!);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ============================================================================
// TYPES
// ============================================================================

// Auth
export interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  org_id: string;
  last_login_at: string | null;
  created_at: string;
  roles?: AdminRole[];
  permissions?: string[];
}

export interface AdminRole {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  hierarchy_level: number;
  is_system_role: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

// Teams
export interface Team {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  parent_team_id: string | null;
  lead_user_id: string | null;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  member_count?: number;
  lead_name?: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: string;
  is_primary: boolean;
  joined_at: string;
  user_name?: string;
  user_email?: string;
}

// Hierarchy
export interface ManagerRelationship {
  id: string;
  user_id: string;
  manager_id: string;
  relationship_type: 'direct' | 'functional' | 'project' | 'dotted_line';
  is_primary: boolean;
  created_at: string;
  user_name?: string;
  manager_name?: string;
}

// Ownership Rules
export interface OwnershipRule {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  entity_type: 'customer' | 'product';
  entity_pattern: string | null;
  assignment_type: 'user' | 'team' | 'role' | 'user_pattern';
  assignment_value: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Audit
export interface AuditLog {
  id: string;
  org_id: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_ip: string | null;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  description: string | null;
  created_at: string;
}

// SIEM
export interface SIEMWebhook {
  id: string;
  org_id: string;
  name: string;
  url: string;
  auth_type: 'none' | 'bearer' | 'basic' | 'api_key';
  format: 'json' | 'cef' | 'leef';
  enabled: boolean;
  last_success_at: string | null;
  last_error: string | null;
  created_at: string;
}

// Compliance
export interface ComplianceReport {
  id: string;
  org_id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  status: string;
  data: Record<string, unknown>;
  summary: string;
  created_at: string;
}

// Retention
export interface RetentionPolicy {
  id: string;
  org_id: string;
  name: string;
  entity_type: string;
  retention_days: number;
  archive_before_delete: boolean;
  is_active: boolean;
  last_run_at: string | null;
  created_at: string;
}

// Simulator
export interface SimulationResult {
  success: boolean;
  affected_users?: Array<{ user_id: string; user_name: string; changes: string[] }>;
  permissions_gained?: string[];
  permissions_lost?: string[];
  warnings?: string[];
}

export interface BulkOwnershipResult {
  action: string;
  total_changes: number;
  by_user: Array<{
    user_id: string;
    user_name: string;
    current_ownerships: number;
    after_ownerships: number;
    changes: Array<{
      entity_type: string;
      entity_name: string;
      change: 'add' | 'remove' | 'unchanged';
    }>;
  }>;
}

// Entity lists
export async function getCustomersList(): Promise<Array<{ id: string; name: string }>> {
  const response = await fetch(`${API_ORIGIN}/api/customers`);
  const data = await response.json();
  return (data.data || data || []).map((c: { id: string; name: string; display_name?: string }) => ({
    id: c.id,
    name: c.display_name || c.name,
  }));
}

export async function getProductsList(): Promise<Array<{ id: string; name: string }>> {
  const response = await fetch(`${API_ORIGIN}/api/products`);
  const data = await response.json();
  return (data.data || data || []).map((p: { id: string; name: string; display_name?: string }) => ({
    id: p.id,
    name: p.display_name || p.name,
  }));
}

// ============================================================================
// AUTH API
// ============================================================================

export async function login(email: string, password: string): Promise<{ user: AdminUser; access_token: string; refresh_token: string }> {
  const result = await adminFetch<{ user: AdminUser; access_token: string; refresh_token: string }>(
    '/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
    false
  );
  setTokens(result.access_token, result.refresh_token);
  return result;
}

export async function logout(): Promise<void> {
  try {
    await adminFetch('/auth/logout', { method: 'POST' });
  } finally {
    clearTokens();
  }
}

export async function getCurrentUser(): Promise<AdminUser> {
  return adminFetch<AdminUser>('/auth/me');
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await adminFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

// ============================================================================
// ORGANIZATIONS API
// ============================================================================

export async function getOrganizations(): Promise<Organization[]> {
  return adminFetch<Organization[]>('/orgs');
}

export async function getOrganization(id: string): Promise<Organization> {
  return adminFetch<Organization>(`/orgs/${id}`);
}

export async function createOrganization(data: { name: string; slug?: string }): Promise<Organization> {
  return adminFetch<Organization>('/orgs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
  return adminFetch<Organization>(`/orgs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteOrganization(id: string): Promise<void> {
  await adminFetch(`/orgs/${id}`, { method: 'DELETE' });
}

// ============================================================================
// ADMIN USERS API
// ============================================================================

export async function getAdminUsers(params?: { status?: string; role?: string; search?: string }): Promise<AdminUser[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.role) searchParams.set('role', params.role);
  if (params?.search) searchParams.set('search', params.search);
  const query = searchParams.toString();
  return adminFetch<AdminUser[]>(`/users${query ? `?${query}` : ''}`);
}

export async function getAdminUser(id: string): Promise<AdminUser> {
  return adminFetch<AdminUser>(`/users/${id}`);
}

export async function createAdminUser(data: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role_ids?: string[];
}): Promise<AdminUser> {
  return adminFetch<AdminUser>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminUser(id: string, data: Partial<AdminUser>): Promise<AdminUser> {
  return adminFetch<AdminUser>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminUser(id: string): Promise<void> {
  await adminFetch(`/users/${id}`, { method: 'DELETE' });
}

export async function assignRole(userId: string, roleId: string): Promise<void> {
  await adminFetch(`/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ role_id: roleId }),
  });
}

export async function revokeRole(userId: string, roleId: string): Promise<void> {
  await adminFetch(`/users/${userId}/roles/${roleId}`, { method: 'DELETE' });
}

export async function getRoles(): Promise<AdminRole[]> {
  return adminFetch<AdminRole[]>('/roles');
}

// ============================================================================
// TEAMS API
// ============================================================================

export async function getTeams(params?: { parent_id?: string; include_members?: boolean }): Promise<Team[]> {
  const searchParams = new URLSearchParams();
  if (params?.parent_id) searchParams.set('parent_id', params.parent_id);
  if (params?.include_members) searchParams.set('include_members', 'true');
  const query = searchParams.toString();
  const response = await fetch(`${API_ORIGIN}/api/teams${query ? `?${query}` : ''}`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to get teams');
  return json.data;
}

export async function getTeamHierarchy(): Promise<Team[]> {
  return adminFetch<Team[]>('/teams/hierarchy');
}

export async function getTeam(id: string): Promise<Team> {
  return adminFetch<Team>(`/teams/${id}`);
}

export async function createTeam(data: {
  name: string;
  description?: string;
  parent_team_id?: string;
  lead_user_id?: string;
}): Promise<Team> {
  const response = await fetch(`${API_ORIGIN}/api/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to create team');
  return json.data;
}

export async function updateTeam(id: string, data: Partial<Team>): Promise<Team> {
  const response = await fetch(`${API_ORIGIN}/api/teams/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to update team');
  return json.data;
}

export async function deleteTeam(id: string): Promise<void> {
  const response = await fetch(`${API_ORIGIN}/api/teams/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const json = await response.json();
    throw new Error(json.error || json.message || 'Failed to delete team');
  }
}

export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  const response = await fetch(`${API_ORIGIN}/api/teams/${teamId}/members`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.message || 'Failed to get team members');
  return json.data;
}

export async function addTeamMember(teamId: string, userId: string, role?: string): Promise<TeamMember> {
  const response = await fetch(`${API_ORIGIN}/api/teams/${teamId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, role }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.message || 'Failed to add team member');
  return json.data;
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  const response = await fetch(`${API_ORIGIN}/api/teams/${teamId}/members/${userId}`, { method: 'DELETE' });
  if (!response.ok) {
    const json = await response.json();
    throw new Error(json.message || 'Failed to remove team member');
  }
}

// ============================================================================
// HIERARCHY API
// ============================================================================

export async function getManagerRelationships(userId?: string): Promise<ManagerRelationship[]> {
  const query = userId ? `?user_id=${userId}` : '';
  return adminFetch<ManagerRelationship[]>(`/hierarchy/relationships${query}`);
}

export async function createManagerRelationship(data: {
  user_id: string;
  manager_id: string;
  relationship_type: string;
  is_primary?: boolean;
}): Promise<ManagerRelationship> {
  return adminFetch<ManagerRelationship>('/hierarchy/relationships', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteManagerRelationship(id: string): Promise<void> {
  await adminFetch(`/hierarchy/relationships/${id}`, { method: 'DELETE' });
}

export async function getUserManagers(userId: string): Promise<ManagerRelationship[]> {
  return adminFetch<ManagerRelationship[]>(`/users/${userId}/managers`);
}

export async function getUserDirectReports(userId: string): Promise<AdminUser[]> {
  return adminFetch<AdminUser[]>(`/users/${userId}/direct-reports`);
}

export async function getOrgChart(): Promise<{ nodes: unknown[]; edges: unknown[] }> {
  return adminFetch('/hierarchy/org-chart');
}

// ============================================================================
// OWNERSHIP RULES API
// ============================================================================

export async function getOwnershipRules(): Promise<OwnershipRule[]> {
  return adminFetch<OwnershipRule[]>('/ownership-rules');
}

export async function getOwnershipRule(id: string): Promise<OwnershipRule> {
  return adminFetch<OwnershipRule>(`/ownership-rules/${id}`);
}

export async function createOwnershipRule(data: {
  name: string;
  description?: string;
  entity_type: string;
  entity_pattern?: string;
  assignment_type: string;
  assignment_value: string;
  priority?: number;
}): Promise<OwnershipRule> {
  return adminFetch<OwnershipRule>('/ownership-rules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOwnershipRule(id: string, data: Partial<OwnershipRule>): Promise<OwnershipRule> {
  return adminFetch<OwnershipRule>(`/ownership-rules/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteOwnershipRule(id: string): Promise<void> {
  await adminFetch(`/ownership-rules/${id}`, { method: 'DELETE' });
}

export async function previewOwnershipRule(data: {
  entity_type: string;
  entity_pattern?: string;
  assignment_type: string;
  assignment_value: string;
}): Promise<{ matches: unknown[]; assignments: unknown[] }> {
  return adminFetch('/ownership-rules/preview', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function executeOwnershipRule(id: string, dryRun: boolean = true): Promise<{ affected: number; details: unknown[] }> {
  return adminFetch(`/ownership-rules/${id}/execute`, {
    method: 'POST',
    body: JSON.stringify({ dry_run: dryRun }),
  });
}

// ============================================================================
// AUDIT API
// ============================================================================

// Note: audit endpoints return { data, total, page, ... } at top level (not wrapped in .data)
// so we use adminFetchRaw to avoid the adminFetch .data unwrapping
async function adminFetchRaw<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('admin_access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options?.headers as Record<string, string>) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${ADMIN_API_BASE}${endpoint}`, { ...options, headers });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || `HTTP ${response.status}`);
  return json as T;
}

export async function searchAuditLogs(params: {
  entity_types?: string[];
  actions?: string[];
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}): Promise<{ logs: AuditLog[]; total: number }> {
  const searchParams = new URLSearchParams();
  // Support multiple entity types and actions
  if (params.entity_types?.length) {
    for (const t of params.entity_types) searchParams.append('entity_type', t);
  }
  if (params.actions?.length) {
    for (const a of params.actions) searchParams.append('action', a);
  }
  if (params.start_date) searchParams.set('from_date', params.start_date);
  if (params.end_date) searchParams.set('to_date', params.end_date);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());
  const qs = searchParams.toString();
  const result = await adminFetchRaw<{ data: AuditLog[]; total: number }>(`/audit${qs ? `?${qs}` : ''}`);
  return { logs: result.data || [], total: result.total || 0 };
}

export async function getAuditStats(params?: {
  start_date?: string;
  end_date?: string;
}): Promise<{
  total_logs: number;
  by_action: Record<string, number>;
  by_entity_type: Record<string, number>;
  by_day: Array<{ date: string; count: number }>;
}> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('from_date', params.start_date);
  if (params?.end_date) searchParams.set('to_date', params.end_date);
  const qs = searchParams.toString();
  return adminFetchRaw(`/audit/stats${qs ? `?${qs}` : ''}`);
}

export async function exportAuditLogs(
  format: 'csv' | 'json',
  params?: { entity_types?: string[]; actions?: string[]; start_date?: string; end_date?: string }
): Promise<void> {
  // Fetch all matching records client-side and trigger download
  const result = await searchAuditLogs({ ...params, limit: 10000 });
  const logs = result.logs;

  if (format === 'json') {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    const headers = ['timestamp', 'actor', 'action', 'entity_type', 'entity_name', 'description'];
    const rows = logs.map(l => [
      l.created_at,
      l.actor_email || 'System',
      l.action,
      l.entity_type,
      l.entity_name || '',
      (l.description || '').replace(/"/g, '""'),
    ].map(v => `"${v}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// ============================================================================
// SIEM WEBHOOKS API
// ============================================================================

export async function getSIEMWebhooks(): Promise<SIEMWebhook[]> {
  return adminFetch<SIEMWebhook[]>('/governance/siem-webhooks');
}

export async function getSIEMWebhook(id: string): Promise<SIEMWebhook> {
  return adminFetch<SIEMWebhook>(`/governance/siem-webhooks/${id}`);
}

export async function createSIEMWebhook(data: {
  name: string;
  url: string;
  auth_type?: string;
  auth_value?: string;
  format?: string;
}): Promise<SIEMWebhook> {
  return adminFetch<SIEMWebhook>('/governance/siem-webhooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSIEMWebhook(id: string, data: Partial<SIEMWebhook>): Promise<SIEMWebhook> {
  return adminFetch<SIEMWebhook>(`/governance/siem-webhooks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSIEMWebhook(id: string): Promise<void> {
  await adminFetch(`/governance/siem-webhooks/${id}`, { method: 'DELETE' });
}

export async function testSIEMWebhook(id: string): Promise<{ success: boolean; response_code?: number; error?: string }> {
  return adminFetch(`/governance/siem-webhooks/${id}/test`, { method: 'POST' });
}

// ============================================================================
// COMPLIANCE REPORTS API
// ============================================================================

export async function getComplianceReports(params?: { report_type?: string }): Promise<ComplianceReport[]> {
  const query = params?.report_type ? `?report_type=${params.report_type}` : '';
  return adminFetch<ComplianceReport[]>(`/governance/compliance-reports${query}`);
}

export async function getComplianceReport(id: string): Promise<ComplianceReport> {
  return adminFetch<ComplianceReport>(`/governance/compliance-reports/${id}`);
}

export async function generateComplianceReport(reportType: string, parameters?: Record<string, unknown>): Promise<ComplianceReport> {
  return adminFetch<ComplianceReport>('/governance/compliance-reports', {
    method: 'POST',
    body: JSON.stringify({ report_type: reportType, parameters }),
  });
}

export async function deleteComplianceReport(id: string): Promise<void> {
  await adminFetch(`/governance/compliance-reports/${id}`, { method: 'DELETE' });
}

// ============================================================================
// RETENTION POLICIES API
// ============================================================================

export async function getRetentionPolicies(): Promise<RetentionPolicy[]> {
  return adminFetch<RetentionPolicy[]>('/governance/retention-policies');
}

export async function getRetentionPolicy(id: string): Promise<RetentionPolicy> {
  return adminFetch<RetentionPolicy>(`/governance/retention-policies/${id}`);
}

export async function createRetentionPolicy(data: {
  name: string;
  entity_type: string;
  retention_days: number;
  archive_before_delete?: boolean;
}): Promise<RetentionPolicy> {
  return adminFetch<RetentionPolicy>('/governance/retention-policies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRetentionPolicy(id: string, data: Partial<RetentionPolicy>): Promise<RetentionPolicy> {
  return adminFetch<RetentionPolicy>(`/governance/retention-policies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRetentionPolicy(id: string): Promise<void> {
  await adminFetch(`/governance/retention-policies/${id}`, { method: 'DELETE' });
}

export async function executeRetentionPolicy(id: string, dryRun: boolean = true): Promise<{
  records_to_delete: number;
  records_deleted: number;
  records_archived: number;
}> {
  return adminFetch(`/governance/retention-policies/${id}/execute`, {
    method: 'POST',
    body: JSON.stringify({ dry_run: dryRun }),
  });
}

// ============================================================================
// SIMULATOR API
// ============================================================================

export async function previewReport(userId: string, reportType: string, weekOffset?: number): Promise<{
  report_type: string;
  user_name: string;
  week_label: string;
  content: string;
  stats: Record<string, unknown>;
}> {
  return adminFetch('/simulator/report-preview', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, report_type: reportType, week_offset: weekOffset }),
  });
}

export async function getReportRecipients(reportType: string = 'all'): Promise<Array<{
  user_id: string;
  user_name: string;
  email: string;
  level: string;
  scope_type: string | null;
}>> {
  return adminFetch(`/simulator/report-recipients/${reportType}`);
}

export async function simulateBulkOwnership(data: {
  action: 'assign' | 'remove';
  user_ids: string[];
  entity_type: string;
  entity_ids: string[];
}): Promise<BulkOwnershipResult> {
  return adminFetch('/simulator/bulk-ownership', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}



export interface RoleChangeResult {
  user_name: string;
  current_permissions: string[];
  after_permissions: string[];
  gained_permissions: string[];
  lost_permissions: string[];
  affected_capabilities: string[];
}

export async function simulateRoleChange(userId: string, addRoles?: string[], removeRoles?: string[]): Promise<RoleChangeResult> {
  return adminFetch('/simulator/role-change', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, add_roles: addRoles, remove_roles: removeRoles }),
  });
}

export async function simulateHierarchyChange(data: {
  user_id: string;
  new_manager_id?: string;
  remove_manager_id?: string;
  relationship_type?: string;
}): Promise<SimulationResult> {
  return adminFetch('/simulator/hierarchy-change', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function runImpactAnalysis(simulations: Array<{
  type: string;
  input: Record<string, unknown>;
}>): Promise<{
  summary: { total_simulations: number; successful: number; failed: number; total_affected_users: number };
  results: Array<{ type: string; input: unknown; result: unknown; error?: string }>;
}> {
  return adminFetch('/simulator/impact-analysis', {
    method: 'POST',
    body: JSON.stringify({ simulations }),
  });
}

// ============================================================================
// BULK OPERATIONS API
// ============================================================================

export async function bulkUpdateUserStatus(userIds: string[], status: string): Promise<{ updated: number }> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/users/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, status }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk status update failed');
  return json;
}

export async function bulkDeleteUsers(userIds: string[]): Promise<{ deleted: number }> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/users/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk delete failed');
  return json;
}

export async function bulkAssignRoles(userIds: string[], roleIds: string[], action: 'assign' | 'revoke'): Promise<{ updated: number }> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/users/roles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, role_ids: roleIds, action }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk role update failed');
  return json;
}

// Bulk customer operations
export async function bulkUpdateCustomerTier(customerIds: string[], tier: string): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/customers/tier`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_ids: customerIds, tier }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk tier update failed');
  return json;
}

export async function bulkAssignCustomerOwners(customerIds: string[], ownerIds: string[]): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/customers/owners`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_ids: customerIds, owner_ids: ownerIds }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk owner assignment failed');
  return json;
}

export async function bulkUpdateCustomerStatus(customerIds: string[], isActive: boolean): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/customers/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_ids: customerIds, is_active: isActive }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk status update failed');
  return json;
}

export async function bulkDeleteCustomers(customerIds: string[]): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/customers/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_ids: customerIds }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk delete failed');
  return json;
}

// Bulk product operations
export async function bulkUpdateProductCategory(productIds: string[], category: string): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/products/category`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_ids: productIds, category }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk category update failed');
  return json;
}

export async function bulkUpdateProductStatus(productIds: string[], isActive: boolean): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/products/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_ids: productIds, is_active: isActive }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk status update failed');
  return json;
}

export async function bulkDeleteProducts(productIds: string[]): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/products/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_ids: productIds }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk delete failed');
  return json;
}

// Bulk employee operations
export async function bulkUpdateEmployeeManager(userIds: string[], managerId: string | null): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/manager`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, manager_id: managerId }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk manager update failed');
  return json;
}

export async function bulkUpdateEmployeeTeam(userIds: string[], teamId: string | null): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/team`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, team_id: teamId }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk team update failed');
  return json;
}

export async function bulkUpdateEmployeeLicensed(userIds: string[], isLicensed: boolean): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/licensed`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, is_licensed: isLicensed }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk licensed update failed');
  return json;
}

export async function bulkUpdateEmployeeStatus(userIds: string[], isActive: boolean): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, is_active: isActive }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk employee status update failed');
  return json;
}

export async function bulkDeleteEmployees(userIds: string[]): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk employee delete failed');
  return json;
}

// User ownerships
export interface UserOwnership {
  id: string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  user_name: string;
}

export async function getOwnershipsByType(entityType: string): Promise<Array<{ user_id: string; user_name: string; entity_id: string; entity_name: string }>> {
  const response = await fetch(`${API_ORIGIN}/api/ownerships?entity_type=${entityType}`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.message || 'Failed to get ownerships');
  return json.data;
}

export async function getUserOwnerships(userId: string): Promise<UserOwnership[]> {
  const response = await fetch(`${API_ORIGIN}/api/users/${userId}/ownerships`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.message || 'Failed to get user ownerships');
  return json.data;
}

export async function setUserOwnerships(userId: string, ownerships: { entity_type: string; entity_id: string }[]): Promise<UserOwnership[]> {
  const response = await fetch(`${API_ORIGIN}/api/users/${userId}/ownerships`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ownerships }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.message || 'Failed to set user ownerships');
  return json.data;
}

// Import operations
export async function importData(type: string, csvContent: string): Promise<ImportResult> {
  return adminFetch(`/import/${type}`, {
    method: 'POST',
    body: JSON.stringify({ csv_content: csvContent }),
  });
}

// Export operations (returns raw CSV text)
export async function exportCSV(type: 'customers' | 'products' | 'employees'): Promise<string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('admin_access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${ADMIN_API_BASE}/export/${type}`, { headers });
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/csv')) {
    throw new Error('Response is not CSV');
  }
  return response.text();
}

// Bulk operation result type
export interface BulkOperationResult {
  success: boolean;
  batch_id: string;
  total: number;
  succeeded: number;
  failed: number;
  errors: Array<{ id: string; message: string }>;
  duration_ms: number;
}

export interface ImportResult {
  success: boolean;
  batch_id: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; field?: string; value?: string; message: string }>;
  duration_ms: number;
}

// ============================================================================
// INVITATIONS API
// ============================================================================

export async function getInvitations(): Promise<Array<{
  id: string;
  email: string;
  role_ids: string[];
  status: string;
  expires_at: string;
  created_at: string;
}>> {
  return adminFetch('/invitations');
}

export async function createInvitation(email: string, roleIds?: string[]): Promise<{ id: string; email: string }> {
  return adminFetch('/invitations', {
    method: 'POST',
    body: JSON.stringify({ email, role_ids: roleIds }),
  });
}

export async function cancelInvitation(id: string): Promise<void> {
  await adminFetch(`/invitations/${id}`, { method: 'DELETE' });
}

export async function resendInvitation(id: string): Promise<void> {
  await adminFetch(`/invitations/${id}/resend`, { method: 'POST' });
}

export async function bulkInvite(emails: string[], roleIds?: string[]): Promise<{ sent: number; failed: string[] }> {
  return adminFetch('/invitations/bulk', {
    method: 'POST',
    body: JSON.stringify({ emails, role_ids: roleIds }),
  });
}

// ============================================================================
// LEVELS API
// ============================================================================

export interface Level {
  id: string;
  name: string;
  display_name: string;
  sort_order: number;
  created_at: string;
}

export async function getLevels(): Promise<Level[]> {
  const response = await fetch(`${API_ORIGIN}/api/levels`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to get levels');
  return json.data;
}

export async function createLevel(data: { name: string; display_name: string; sort_order?: number }): Promise<Level> {
  const response = await fetch(`${API_ORIGIN}/api/levels`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to create level');
  return json.data;
}

export async function updateLevel(id: string, data: { display_name?: string; sort_order?: number }): Promise<Level> {
  const response = await fetch(`${API_ORIGIN}/api/levels/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Failed to update level');
  return json.data;
}

export async function deleteLevel(id: string): Promise<void> {
  const response = await fetch(`${API_ORIGIN}/api/levels/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const json = await response.json();
    throw new Error(json.error || json.message || 'Failed to delete level');
  }
}

// ============================================================================
// SCOPE TYPE API
// ============================================================================

export async function updateScopeType(userId: string, scopeType: 'customer' | 'product' | null, scopeAll?: boolean): Promise<void> {
  const body: Record<string, unknown> = { scope_type: scopeType };
  if (scopeAll !== undefined) body.scope_all = scopeAll;
  const response = await fetch(`${API_ORIGIN}/api/users/${userId}/scope-type`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const json = await response.json();
    throw new Error(json.message || 'Failed to update scope type');
  }
}

export async function updateScopeAll(userId: string, scopeAll: boolean): Promise<void> {
  const response = await fetch(`${API_ORIGIN}/api/users/${userId}/scope-type`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope_all: scopeAll }),
  });
  if (!response.ok) {
    const json = await response.json();
    throw new Error(json.message || 'Failed to update scope all');
  }
}

// ============================================================================
// BULK EMPLOYEE LEVEL
// ============================================================================

export async function bulkUpdateEmployeeLevel(userIds: string[], level: string): Promise<BulkOperationResult> {
  const response = await fetch(`${ADMIN_API_BASE}/bulk/employees/level`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds, level }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || json.message || 'Bulk level update failed');
  return json;
}

// ============================================================================
// DELIVERY MONITORING API
// ============================================================================

export interface DeliveryTeamSummary {
  team_id: string | null;
  team_name: string;
  delivered: number;
  failed: number;
  pending: number;
  total_users: number;
}

export interface DeliveryStatusResponse {
  summary: { delivered: number; failed: number; pending: number };
  teams: DeliveryTeamSummary[];
}

export interface DeliveryUser {
  id: string;
  user_id: string;
  user_name: string;
  report_type: string;
  status: string;
  source?: string;
  delivered_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface DeliveryUserContent {
  id: string;
  report_type: string;
  status: string;
  source?: string;
  content: unknown;
  formatted_text: string;
  delivered_at: string | null;
  error_message: string | null;
  created_at: string;
  user_name: string;
}

export async function getDeliveryStatus(year: number, week: number): Promise<DeliveryStatusResponse> {
  return adminFetch<DeliveryStatusResponse>(`/delivery-status?year=${year}&week=${week}`);
}

export async function getDeliveryTeamDetails(
  year: number, week: number, teamId: string | null, status?: string
): Promise<DeliveryUser[]> {
  const params = new URLSearchParams({ year: String(year), week: String(week) });
  params.set('team_id', teamId ?? 'null');
  if (status) params.set('status', status);
  return adminFetch<DeliveryUser[]>(`/delivery-status/team?${params}`);
}

export async function getDeliveryUserContent(
  year: number, week: number, userId: string
): Promise<DeliveryUserContent[]> {
  return adminFetch<DeliveryUserContent[]>(
    `/delivery-status/user?year=${year}&week=${week}&user_id=${userId}`
  );
}

export interface DeliveryWeeksResponse {
  weeks: number[];
  first_year: number;
  first_week: number;
}

export async function getDeliveryWeeks(year: number): Promise<DeliveryWeeksResponse> {
  return adminFetch<DeliveryWeeksResponse>(`/delivery-status/weeks?year=${year}`);
}

export async function getDeliveryYears(): Promise<number[]> {
  return adminFetch<number[]>(`/delivery-status/years`);
}

// ============================================================================
// Delivery Diagnostics
// ============================================================================

export interface SlackHealth {
  connected: boolean;
  bot_name?: string;
  workspace?: string;
  bot_id?: string;
  error?: string;
}

export interface UserDiagnostics {
  user: { id: string; name: string; email: string; scope_type: string | null; slack_id: string | null } | null;
  slack_valid: boolean | null;
  slack_error?: string;
  ownership_counts: { customers: number; products: number };
  recent_deliveries: Array<{
    id: string;
    report_type: string;
    year: number;
    week: number;
    status: string;
    error_message: string | null;
    delivered_at: string | null;
    created_at: string;
  }>;
}

export async function getSlackHealth(): Promise<SlackHealth> {
  return adminFetch<SlackHealth>('/simulator/slack-health');
}

export async function getUserDiagnostics(userId: string): Promise<UserDiagnostics> {
  return adminFetch<UserDiagnostics>(`/simulator/user-diagnostics/${userId}`);
}

export async function retryDelivery(deliveryId: string): Promise<{ success: boolean; error?: string }> {
  return adminFetch<{ success: boolean; error?: string }>(`/simulator/retry-delivery/${deliveryId}`, {
    method: 'POST',
  });
}

export async function sendReport(
  userId: string,
  reportType: string,
  content: unknown,
  formattedText: string,
): Promise<{ success: boolean; delivery_id?: string; error?: string }> {
  return adminFetch<{ success: boolean; delivery_id?: string; error?: string }>('/simulator/send-report', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, report_type: reportType, content, formatted_text: formattedText }),
  });
}

export interface SchedulerPreviewItem {
  user_id: string;
  user_name: string;
  report_type: string;
  has_data: boolean;
  current_delivery_status: string | null;
  scope_all: boolean;
}

export async function getSchedulerPreview(weekOffset: number = 0): Promise<SchedulerPreviewItem[]> {
  return adminFetch<SchedulerPreviewItem[]>(`/simulator/scheduler-preview?week_offset=${weekOffset}`);
}

export async function schedulerSendAll(weekOffset: number, userIds?: string[]): Promise<{ total: number; succeeded: number; failed: number; results: Array<{ user_name: string; report_type: string; success: boolean; error?: string }> }> {
  return adminFetch('/simulator/scheduler-send-all', {
    method: 'POST',
    body: JSON.stringify({ week_offset: weekOffset, user_ids: userIds }),
  });
}

// ============================================================================
// SETTINGS API
// ============================================================================

export interface OrgSettings {
  slack: {
    delivery_day: string;            // Report Delivery — day of week
    delivery_time: string;           // Report Delivery — HH:MM
    rollup_reminder_day: string;     // Manager Reminder — day of week
    rollup_reminder_time: string;    // Manager Reminder — HH:MM
    timezone: string;
    default_channel_id: string | null;
  };
  features: {
    devtools_enabled: boolean;
    audit_export_enabled: boolean;
    submission_anomaly_threshold: number;
  };
  env_defaults: {
    rollup_schedule: string;
    rollup_timezone: string;
  };
}

export async function getOrgSettings(): Promise<OrgSettings> {
  return adminFetch<OrgSettings>('/settings');
}

export async function updateOrgSettings(settings: Record<string, unknown>): Promise<void> {
  await adminFetch('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// SB-4: Sync users from Slack. Backend route requires a `slack_token` in
// the body today; per-tenant OAuth-stored token fallback is Phase 1
// (SB-8 wires the credential cipher into Command Center). Until then the
// caller passes an explicit token.
export interface SyncFromSlackInput {
  slack_token: string;
  sync_options?: {
    include_bots?: boolean;
    include_guests?: boolean;
    include_deactivated?: boolean;
    auto_assign_role?: string;
  };
}

export interface SyncFromSlackResult {
  synced: number;
  added: number;
  updated: number;
  errors: Array<{ slack_id: string; error: string }>;
}

export async function syncFromSlack(input: SyncFromSlackInput): Promise<SyncFromSlackResult> {
  return adminFetch<SyncFromSlackResult>('/sync/slack', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getSchedulerHealth(): Promise<{
  running: boolean;
  schedule: string;
  reportsSchedule: string;
  timezone: string;
}> {
  const response = await fetch(`${API_ORIGIN}/api/admin/scheduler`);
  const json = await response.json();
  return json.data || json;
}

export async function triggerRollups(): Promise<void> {
  const response = await fetch(`${API_ORIGIN}/api/admin/rollups/trigger`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to trigger rollups');
}

export async function getAiHealth(): Promise<{
  configured: boolean;
  provider?: string;
  model?: string;
  error?: string;
}> {
  return adminFetch<{ configured: boolean; provider?: string; model?: string; error?: string }>('/simulator/ai-health');
}

// ============================================================================
// Submission Monitor — inbound mirror of Delivery Monitor
// ============================================================================

export type SubmissionStatus = 'submitted' | 'pending' | 'overdue';
export type AnomalyLevel = 'normal' | 'low' | 'silent';

export interface SubmissionTeamRow {
  team_id: string | null;
  team_name: string;
  eligible: number;
  submitted: number;
  pending: number;
  overdue: number;
  coverage_pct: number;
}

export interface SubmissionStatusResponse {
  summary: {
    eligible: number;
    submitted: number;
    pending: number;
    overdue: number;
    coverage_pct: number;
  };
  teams: SubmissionTeamRow[];
  week_cutoff: string;
  past_cutoff: boolean;
}

export interface SubmissionUserRow {
  user_id: string;
  user_name: string;
  scope_type: string | null;
  team_id: string | null;
  team_name: string | null;
  status: SubmissionStatus;
  last_submitted_at: string | null;
  submission_count: number;
}

export interface UserSubmissionDetail {
  report_id: string;
  customer_id: string;
  customer_name: string;
  product_names: string[];
  engagement_type: string;
  sentiment: string;
  notes: string;
  created_at: string;
}

export interface RecentSubmission {
  user_id: string;
  user_name: string;
  team_name: string | null;
  customer_name: string;
  product_names: string[];
  engagement_type: string;
  created_at: string;
}

export interface SubmissionHealthResponse {
  today_count: number;
  avg_last_4_weeks_same_day: number;
  anomaly: AnomalyLevel;
  threshold_pct: number;
}

export interface SubmissionWeeksResponse {
  weeks: number[];
  first_year: number;
  first_week: number;
}

export interface NudgeResultResponse {
  nudged: Array<{ user_id: string; user_name: string }>;
  skipped: Array<{ user_id: string; user_name: string; reason: string }>;
  failed: Array<{ user_id: string; user_name: string; error: string }>;
}

export async function getSubmissionStatus(year: number, week: number): Promise<SubmissionStatusResponse> {
  return adminFetch<SubmissionStatusResponse>(`/submissions/status?year=${year}&week=${week}`);
}

export async function getSubmissionTeamDetails(
  year: number,
  week: number,
  teamId: string | null | undefined,
  status?: SubmissionStatus,
): Promise<SubmissionUserRow[]> {
  const params = new URLSearchParams({ year: String(year), week: String(week) });
  if (teamId !== undefined) params.set('team_id', teamId ?? '');
  if (status) params.set('status', status);
  return adminFetch<SubmissionUserRow[]>(`/submissions/team?${params}`);
}

/**
 * Fetch every eligible user across all teams in one shot (used by global search).
 * Returns the same shape as getSubmissionTeamDetails but ignores team boundaries.
 */
export async function getAllUserSubmissions(
  year: number,
  week: number,
): Promise<SubmissionUserRow[]> {
  return adminFetch<SubmissionUserRow[]>(`/submissions/team?year=${year}&week=${week}`);
}

export async function getUserSubmissions(
  year: number,
  week: number,
  userId: string,
): Promise<UserSubmissionDetail[]> {
  return adminFetch<UserSubmissionDetail[]>(
    `/submissions/user?year=${year}&week=${week}&user_id=${userId}`,
  );
}

export async function getSubmissionWeeks(year: number): Promise<SubmissionWeeksResponse> {
  return adminFetch<SubmissionWeeksResponse>(`/submissions/weeks?year=${year}`);
}

export async function getSubmissionYears(): Promise<number[]> {
  return adminFetch<number[]>('/submissions/years');
}

export async function getRecentSubmissions(limit = 10): Promise<RecentSubmission[]> {
  return adminFetch<RecentSubmission[]>(`/submissions/recent?limit=${limit}`);
}

export async function getSubmissionHealth(): Promise<SubmissionHealthResponse> {
  return adminFetch<SubmissionHealthResponse>('/submissions/health');
}

export async function getUserSubmissionHistory(
  userId: string,
  weeks = 8,
): Promise<Array<{ year: number; week: number; submission_count: number }>> {
  return adminFetch<Array<{ year: number; week: number; submission_count: number }>>(
    `/submissions/user/${userId}/history?weeks=${weeks}`,
  );
}

export async function sendSubmissionNudge(
  userIds: string[],
  options: { year?: number; week?: number; customMessage?: string } = {},
): Promise<NudgeResultResponse> {
  return adminFetch<NudgeResultResponse>('/submissions/nudge', {
    method: 'POST',
    body: JSON.stringify({
      user_ids: userIds,
      year: options.year,
      week: options.week,
      custom_message: options.customMessage,
    }),
  });
}

// ============================================================================
// Manager Rollup Lock Status — Stage 2 gate between submission and delivery
// ============================================================================

export interface ManagerLockRow {
  manager_id: string;
  manager_name: string;
  manager_email: string;
  manager_slack_id: string | null;
  direct_report_count: number;
  reports_in_week: number;
  locked: boolean;
  locked_at: string | null;
  locked_report_count: number;
  sent_to_manager_id: string | null;
  sent_to_manager_name: string | null;
  source: 'slack' | 'force_lock' | null;
}

export interface RollupLockStatusResponse {
  year: number;
  week: number;
  summary: { total_managers: number; locked: number; unlocked: number };
  managers: ManagerLockRow[];
}

export interface ForceLockResult {
  manager_id: string;
  manager_name: string;
  locked: boolean;
  locked_report_count: number;
  notified_upstream: boolean;
  upstream_manager_name: string | null;
  warnings: string[];
}

export interface UnlockResult {
  manager_id: string;
  manager_name: string;
  unlocked: boolean;
}

export async function getRollupLockStatus(year: number, week: number): Promise<RollupLockStatusResponse> {
  return adminFetch<RollupLockStatusResponse>(`/rollup-locks?year=${year}&week=${week}`);
}

export async function forceLockRollup(
  managerId: string,
  year: number,
  week: number,
  notifyUpstream = false,
): Promise<ForceLockResult> {
  return adminFetch<ForceLockResult>('/rollup-locks/force-lock', {
    method: 'POST',
    body: JSON.stringify({ manager_id: managerId, year, week, notify_upstream: notifyUpstream }),
  });
}

export async function unlockRollup(managerId: string, year: number, week: number): Promise<UnlockResult> {
  return adminFetch<UnlockResult>('/rollup-locks', {
    method: 'DELETE',
    body: JSON.stringify({ manager_id: managerId, year, week }),
  });
}
